import { useEffect, useState } from 'react';
import type { ReactElement } from 'react';
import { Link as RouterLink, Outlet, useLocation, useNavigate } from 'react-router-dom';
import useMediaQuery from '@mui/material/useMediaQuery';
import type { Theme } from '@mui/material/styles';
import AppBar from '@mui/material/AppBar';
import Avatar from '@mui/material/Avatar';
import Box from '@mui/material/Box';
import Collapse from '@mui/material/Collapse';
import Divider from '@mui/material/Divider';
import Drawer from '@mui/material/Drawer';
import IconButton from '@mui/material/IconButton';
import List from '@mui/material/List';
import ListItemButton from '@mui/material/ListItemButton';
import ListItemIcon from '@mui/material/ListItemIcon';
import ListItemText from '@mui/material/ListItemText';
import Menu from '@mui/material/Menu';
import MenuItem from '@mui/material/MenuItem';
import Stack from '@mui/material/Stack';
import Toolbar from '@mui/material/Toolbar';
import Tooltip from '@mui/material/Tooltip';
import Typography from '@mui/material/Typography';
import DashboardIcon from '@mui/icons-material/DashboardOutlined';
import DomainIcon from '@mui/icons-material/DomainOutlined';
import CorporateFareIcon from '@mui/icons-material/CorporateFareOutlined';
import HistoryIcon from '@mui/icons-material/HistoryOutlined';
import TuneIcon from '@mui/icons-material/TuneOutlined';
import PeopleIcon from '@mui/icons-material/PeopleOutlined';
import RuleIcon from '@mui/icons-material/RuleOutlined';
import RequestQuoteIcon from '@mui/icons-material/RequestQuoteOutlined';
import ShoppingCartIcon from '@mui/icons-material/ShoppingCartOutlined';
import LocalShippingIcon from '@mui/icons-material/LocalShippingOutlined';
import MoveToInboxIcon from '@mui/icons-material/MoveToInboxOutlined';
import FactCheckIcon from '@mui/icons-material/FactCheckOutlined';
import ViewModuleIcon from '@mui/icons-material/ViewModuleOutlined';
import AssignmentIcon from '@mui/icons-material/AssignmentOutlined';
import PlaylistAddCheckIcon from '@mui/icons-material/PlaylistAddCheckOutlined';
import PrecisionManufacturingIcon from '@mui/icons-material/PrecisionManufacturingOutlined';
import HandymanIcon from '@mui/icons-material/HandymanOutlined';
import ListAltIcon from '@mui/icons-material/ListAltOutlined';
import OutputIcon from '@mui/icons-material/OutputOutlined';
import InventoryIcon from '@mui/icons-material/InventoryOutlined';
import Inventory2Icon from '@mui/icons-material/Inventory2Outlined';
import AllInboxIcon from '@mui/icons-material/AllInboxOutlined';
import OutboundIcon from '@mui/icons-material/OutboundOutlined';
import BalanceIcon from '@mui/icons-material/BalanceOutlined';
import SwapHorizIcon from '@mui/icons-material/SwapHorizOutlined';
import AssignmentTurnedInIcon from '@mui/icons-material/AssignmentTurnedInOutlined';
import UndoIcon from '@mui/icons-material/UndoOutlined';
import AssessmentIcon from '@mui/icons-material/AssessmentOutlined';
import ReceiptLongIcon from '@mui/icons-material/ReceiptLongOutlined';
import DescriptionOutlinedIcon from '@mui/icons-material/DescriptionOutlined';
import LocalPrintshopIcon from '@mui/icons-material/LocalPrintshopOutlined';
import ScaleIcon from '@mui/icons-material/ScaleOutlined';
import MonitorWeightIcon from '@mui/icons-material/MonitorWeightOutlined';
import SettingsInputComponentIcon from '@mui/icons-material/SettingsInputComponentOutlined';
import MonitorHeartIcon from '@mui/icons-material/MonitorHeartOutlined';
import ExitToAppIcon from '@mui/icons-material/ExitToAppOutlined';
import LogoutIcon from '@mui/icons-material/LogoutOutlined';
import PersonOutlineIcon from '@mui/icons-material/PersonOutline';
import LightModeOutlinedIcon from '@mui/icons-material/LightModeOutlined';
import DarkModeOutlinedIcon from '@mui/icons-material/DarkModeOutlined';
import TvOutlinedIcon from '@mui/icons-material/TvOutlined';
import ChevronLeftIcon from '@mui/icons-material/ChevronLeft';
import ChevronRightIcon from '@mui/icons-material/ChevronRight';
import MenuIcon from '@mui/icons-material/MenuOutlined';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import { useTranslation } from 'react-i18next';
import { useAuth } from '@/contexts/AuthContext';
import { useActiveCompany } from '@/contexts/ActiveCompanyContext';
import { useThemeMode } from '@/contexts/ThemeModeContext';
import { useProcessConfiguration } from '@/hooks/useProcessConfiguration';
import LanguageSwitcher from '@/components/common/LanguageSwitcher';
import BrandMark from '@/components/common/BrandMark';
import NetworkStatusIndicator from '@/components/common/NetworkStatusIndicator';
import NotificationBell from '@/components/common/NotificationBell';
import { PERMISSIONS, ROLES, getInitials } from '@/types/auth';
import type { Permission } from '@/types/auth';
import type { ProcessConfigurationDto } from '@/types/configuration';

const DRAWER_WIDTH = 264;
const DRAWER_WIDTH_COLLAPSED = 84;
const SIDEBAR_COLLAPSED_KEY = 'ims.sidebarCollapsed';
// One fixed row height shared by every sidebar row — top-level links, group headers, and
// sub-items alike — so the rhythm going down the sidebar reads as consistent instead of each
// tier having its own slightly different padding-driven height.
const NAV_ROW_HEIGHT = 42;

// Categorical accent per nav group — reuses the app's existing palette colors (no new hues
// introduced) cycling through them, purely to give the sidebar's grouped sections the same
// "each category has its own accent" wayfinding structure as the reference template.
type Accent = 'primary' | 'secondary' | 'warning' | 'error';

interface NavLeaf {
  labelKey: string;
  to: string;
  icon: ReactElement;
  // Array = "any of" (matches AuthContext.hasPermission) — used by the single "Masters" entry
  // below, which should show up if the user can see at least one of its tabs.
  permission?: Permission | Permission[];
  // The ProcessConfiguration boolean flag(s) that gate this step — when the admin turns one off in
  // Process Configuration, the step is skipped end-to-end (per the config-driven design), so its
  // nav entry disappears too rather than linking to a workflow that's currently disabled. An array
  // requires every flag to be true (e.g. the Production Receipt steps need both the section's own
  // master productionEnabled switch AND their own productionReceiptEnabled to be on).
  configKey?: keyof ProcessConfigurationDto | (keyof ProcessConfigurationDto)[];
  // OR-group companion to configKey — visible if ANY of these are true (vs configKey's "every one
  // must be true"), evaluated as an extra AND-ed condition alongside configKey/hiddenWhenConfigKey.
  // Production Plan and Material Request are independent sub-flows (a company can run either,
  // both, or neither), but Production Receipt is common to both — it needs at least one of the two
  // active to have anything to receive against, not specifically Production Plan.
  anyConfigKey?: (keyof ProcessConfigurationDto)[];
  // Inverse of configKey — the item is hidden whenever ANY of these flags is true, instead of
  // requiring them all to be true to show. Used for screens that stop being relevant once a
  // company switches into 3PL mode (Purchase Request/Order, the whole Production module) — a 3PL
  // warehouse receives client-owned goods via GRN rather than purchasing its own inventory, and
  // typically doesn't manufacture.
  hiddenWhenConfigKey?: keyof ProcessConfigurationDto | (keyof ProcessConfigurationDto)[];
  // Role gate (separate from permission) — e.g. the cross-tenant Companies console must stay
  // hidden from an ordinary company Administrator even though they also hold Company.Manage for
  // editing their own company.
  role?: string;
  // Escape hatch for a visibility rule the boolean-flag AND/OR gates above can't express — e.g.
  // "gated by a string-enum config field taking a particular value or set of values", not just a
  // flag being truthy. Undefined always passes, same fallback convention as the other gates;
  // config is undefined until Process Configuration has loaded, mirroring their own guard.
  customGate?: (config: ProcessConfigurationDto | undefined) => boolean;
}

// New modules land here as their pages are built out phase by phase — kept intentionally short
// right now rather than linking to screens that don't exist yet.
const NAV_ITEMS: NavLeaf[] = [
  { labelKey: 'dashboard', to: '/dashboard', icon: <DashboardIcon fontSize="small" /> },
  {
    labelKey: 'masters',
    to: '/masters',
    icon: <DomainIcon fontSize="small" />,
    permission: [
      PERMISSIONS.COMPANY_VIEW,
      PERMISSIONS.WAREHOUSE_VIEW,
      PERMISSIONS.MATERIAL_VIEW,
      PERMISSIONS.SUPPLIER_VIEW,
      PERMISSIONS.CUSTOMER_VIEW,
    ],
  },
  {
    labelKey: 'purchaseRequest',
    to: '/transactions/purchase-requests',
    icon: <RequestQuoteIcon fontSize="small" />,
    permission: PERMISSIONS.PURCHASE_REQUEST_VIEW,
    configKey: 'purchaseRequestEnabled',
    // A 3PL warehouse receives client-owned goods via GRN rather than purchasing its own inventory.
    hiddenWhenConfigKey: 'thirdPartyLogisticsEnabled',
  },
  {
    labelKey: 'purchaseOrder',
    to: '/transactions/purchase-orders',
    icon: <ShoppingCartIcon fontSize="small" />,
    permission: PERMISSIONS.PURCHASE_ORDER_VIEW,
    configKey: 'purchaseOrderEnabled',
    hiddenWhenConfigKey: 'thirdPartyLogisticsEnabled',
  },
  {
    labelKey: 'gateEntry',
    to: '/transactions/gate-entries',
    icon: <LocalShippingIcon fontSize="small" />,
    permission: PERMISSIONS.GATE_ENTRY_VIEW,
    configKey: 'gateEntryEnabled',
  },
  {
    // Hidden once a Weigh & Label plant switches Raw Material Handling off (Direct-only plans,
    // nothing consumed via BOM) — with no raw material ever issued against a plan, there's nothing
    // for a Goods Receipt to feed. weightProductionRawMaterialHandlingEnabled defaults true for
    // every company (including ones that never touch Weigh & Label at all — see its own migration
    // note), so this is a no-op everywhere except that specific state.
    labelKey: 'grn',
    to: '/transactions/grn',
    icon: <MoveToInboxIcon fontSize="small" />,
    permission: PERMISSIONS.GRN_VIEW,
    configKey: 'weightProductionRawMaterialHandlingEnabled',
  },
  {
    labelKey: 'qc',
    to: '/transactions/quality-control',
    icon: <FactCheckIcon fontSize="small" />,
    permission: PERMISSIONS.QC_VIEW,
    configKey: ['qcEnabled', 'weightProductionRawMaterialHandlingEnabled'],
  },
  {
    labelKey: 'putaway',
    to: '/transactions/putaway',
    icon: <ViewModuleIcon fontSize="small" />,
    permission: PERMISSIONS.PUTAWAY_VIEW,
    configKey: ['putawayEnabled', 'weightProductionRawMaterialHandlingEnabled'],
  },
  {
    // The receiving half of Dispatch's Warehouse destination option (see
    // DispatchOrder.DestinationType) — only relevant once that destination type is actually
    // offered, so it's gated on Process Configuration's Dispatch Type including "Warehouse",
    // independent of Weigh & Label (see ProcessConfiguration.DispatchTypeScope's own doc comment).
    labelKey: 'warehouseTransferReceipt',
    to: '/transactions/warehouse-transfer-receipts',
    icon: <AllInboxIcon fontSize="small" />,
    permission: PERMISSIONS.PICKING_VIEW,
    customGate: (config) => !config || config.dispatchTypeScope !== 'Customer',
  },
  {
    // Independent of Production Plan (productionEnabled) — a company can choose either, both, or
    // neither — but both live under the section's common productionModuleEnabled master, off
    // hiding this regardless of materialRequestEnabled's own state.
    labelKey: 'materialRequest',
    to: '/transactions/material-requests',
    icon: <AssignmentIcon fontSize="small" />,
    permission: PERMISSIONS.MATERIAL_REQUEST_VIEW,
    configKey: ['productionModuleEnabled', 'materialRequestEnabled'],
  },
  {
    // Picking is raised from a Material Request's pick list, so it lives right next to it in the
    // nav and shares its visibility switch — no Material Request screen means no pick lists to
    // work from, so Picking has nothing to do either.
    labelKey: 'picking',
    to: '/transactions/picking',
    icon: <PlaylistAddCheckIcon fontSize="small" />,
    permission: PERMISSIONS.PICKING_VIEW,
    configKey: ['productionModuleEnabled', 'materialRequestEnabled'],
  },
  {
    labelKey: 'productionOrders',
    to: '/transactions/production-orders',
    icon: <PrecisionManufacturingIcon fontSize="small" />,
    permission: PERMISSIONS.PRODUCTION_VIEW,
    configKey: ['productionModuleEnabled', 'productionEnabled'],
    // A pure 3PL warehouse stores/moves client-owned goods rather than manufacturing them.
    hiddenWhenConfigKey: 'thirdPartyLogisticsEnabled',
  },
  {
    labelKey: 'productionPickList',
    to: '/transactions/production-pick-list',
    icon: <ListAltIcon fontSize="small" />,
    permission: PERMISSIONS.PRODUCTION_VIEW,
    configKey: ['productionModuleEnabled', 'productionEnabled'],
    hiddenWhenConfigKey: 'thirdPartyLogisticsEnabled',
  },
  {
    labelKey: 'materialIssue',
    to: '/transactions/material-issue',
    icon: <OutputIcon fontSize="small" />,
    permission: PERMISSIONS.PRODUCTION_VIEW,
    configKey: ['productionModuleEnabled', 'productionEnabled'],
    hiddenWhenConfigKey: 'thirdPartyLogisticsEnabled',
  },
  {
    // Common to BOTH independent sub-flows — Production Plan and Material Request each feed their
    // own tab on this same screen (ProductionReceiptPage.tsx switches between receiving against a
    // Production Order vs a completed Material Request), so Receipt only needs at least ONE of the
    // two active to have anything to receive against, not specifically Production Plan. Its own
    // productionReceiptEnabled sub-flag AND the section's common productionModuleEnabled master
    // are still required outright.
    labelKey: 'productionReceipt',
    to: '/transactions/production-receipt',
    icon: <InventoryIcon fontSize="small" />,
    permission: PERMISSIONS.PRODUCTION_VIEW,
    configKey: ['productionModuleEnabled', 'productionReceiptEnabled'],
    anyConfigKey: ['productionEnabled', 'materialRequestEnabled'],
    hiddenWhenConfigKey: 'thirdPartyLogisticsEnabled',
  },
  {
    // Downstream of Production Receipt, same "common to both sources" reasoning — a company
    // running only Material Request (no Production Plan) still needs to label/putaway what it
    // received, so this shouldn't require productionEnabled specifically either.
    labelKey: 'productionLabelPrint',
    to: '/transactions/production-label-printing',
    icon: <LocalPrintshopIcon fontSize="small" />,
    permission: PERMISSIONS.PRODUCTION_VIEW,
    // Barcode Required is the sole switch for every label-print screen — off means nothing
    // downstream (Putaway, Dispatch) ever checks a barcode, so there's no reason to print one
    // either (see labelPrinting's own identical rationale below); on means it's always available,
    // never independently toggle-off-able.
    configKey: ['productionModuleEnabled', 'productionReceiptEnabled', 'barcodeMandatory'],
    anyConfigKey: ['productionEnabled', 'materialRequestEnabled'],
    hiddenWhenConfigKey: 'thirdPartyLogisticsEnabled',
  },
  {
    // Same reasoning as Production Label Print above.
    labelKey: 'productionPutaway',
    to: '/transactions/production-putaway',
    icon: <ViewModuleIcon fontSize="small" />,
    permission: PERMISSIONS.PRODUCTION_VIEW,
    configKey: ['productionModuleEnabled', 'productionReceiptEnabled'],
    anyConfigKey: ['productionEnabled', 'materialRequestEnabled'],
    hiddenWhenConfigKey: 'thirdPartyLogisticsEnabled',
  },
  {
    // Job Work — its own module master switch, off by default. Unlike Production, this is NOT
    // hidden under 3PL: a 3PL warehouse can legitimately send a client's goods out for processing.
    labelKey: 'jobWorkOrders',
    to: '/transactions/job-work-orders',
    icon: <HandymanIcon fontSize="small" />,
    permission: PERMISSIONS.JOB_WORK_VIEW,
    configKey: 'jobWorkEnabled',
  },
  {
    labelKey: 'jobWorkIssue',
    to: '/transactions/job-work-issue',
    icon: <OutboundIcon fontSize="small" />,
    permission: PERMISSIONS.JOB_WORK_VIEW,
    configKey: 'jobWorkEnabled',
  },
  {
    labelKey: 'jobWorkReceipt',
    to: '/transactions/job-work-receipt',
    icon: <MoveToInboxIcon fontSize="small" />,
    permission: PERMISSIONS.JOB_WORK_VIEW,
    configKey: 'jobWorkEnabled',
  },
  {
    // Only ever has anything to inspect when QC is on for this company — mirrors GRN's own qc leaf
    // gating (configKey: 'qcEnabled'), since a job work return follows the same QC decision.
    labelKey: 'jobWorkQc',
    to: '/transactions/job-work-qc',
    icon: <FactCheckIcon fontSize="small" />,
    permission: PERMISSIONS.JOB_WORK_VIEW,
    configKey: ['jobWorkEnabled', 'qcEnabled'],
  },
  {
    // Barcode Required is the sole switch for every label-print screen (see productionLabelPrint's
    // own identical rationale) — no independent manual toggle for Job Work Label Print either.
    labelKey: 'jobWorkLabelPrint',
    to: '/transactions/job-work-label-printing',
    icon: <LocalPrintshopIcon fontSize="small" />,
    permission: PERMISSIONS.JOB_WORK_VIEW,
    configKey: ['jobWorkEnabled', 'barcodeMandatory'],
  },
  {
    // Mirrors GRN's own putaway leaf gating (configKey: 'putawayEnabled').
    labelKey: 'jobWorkPutaway',
    to: '/transactions/job-work-putaway',
    icon: <ViewModuleIcon fontSize="small" />,
    permission: PERMISSIONS.JOB_WORK_VIEW,
    configKey: ['jobWorkEnabled', 'putawayEnabled'],
  },
  {
    // Weight-Based Production — its own module master switch, off by default, sibling to Job Work
    // (not a mode of standard Production — see Domain/WeightProduction's module doc).
    labelKey: 'weightProductionPlan',
    to: '/transactions/weight-production-plan',
    icon: <ScaleIcon fontSize="small" />,
    permission: PERMISSIONS.WEIGHT_PRODUCTION_VIEW,
    configKey: 'weightProductionEnabled',
  },
  {
    // Only BOM-mode plans ever need issuing — and since RawMaterialHandlingEnabled now decides,
    // company-wide, whether BOM mode exists at all (see WeightProductionOrdersPage's tab gating), a
    // company with it off can never have a BOM plan to issue against. Hidden in that state; shown
    // whenever it's on, so a released BOM plan always has a visible way to issue its material.
    labelKey: 'weightProductionIssue',
    to: '/transactions/weight-production-issue',
    icon: <OutboundIcon fontSize="small" />,
    permission: PERMISSIONS.WEIGHT_PRODUCTION_VIEW,
    configKey: ['weightProductionEnabled', 'weightProductionRawMaterialHandlingEnabled'],
  },
  {
    labelKey: 'weightProductionKiosk',
    to: '/transactions/weight-production-kiosk',
    icon: <MonitorWeightIcon fontSize="small" />,
    permission: PERMISSIONS.WEIGHT_PRODUCTION_EXECUTE,
    configKey: 'weightProductionEnabled',
  },
  {
    // Supervisor-level confirm-to-post-stock step for plan-based weighings — see
    // WeightProductionJobCompletionPage's own doc comment.
    labelKey: 'weightProductionJobCompletion',
    to: '/transactions/weight-production-job-completion',
    icon: <AssignmentTurnedInIcon fontSize="small" />,
    permission: PERMISSIONS.WEIGHT_PRODUCTION_VIEW,
    configKey: 'weightProductionEnabled',
  },
  {
    // Read-only — see WeightProductionPutawayPage's doc comment for why there's nothing to confirm.
    labelKey: 'weightProductionPutaway',
    to: '/transactions/weight-production-putaway',
    icon: <AllInboxIcon fontSize="small" />,
    permission: PERMISSIONS.WEIGHT_PRODUCTION_VIEW,
    configKey: 'weightProductionEnabled',
  },
  {
    labelKey: 'weightProductionLabelPrint',
    to: '/printing/weight-production-label-print',
    icon: <LocalPrintshopIcon fontSize="small" />,
    permission: PERMISSIONS.WEIGHT_PRODUCTION_VIEW,
    configKey: 'weightProductionEnabled',
  },
  {
    labelKey: 'hardwareConfiguration',
    to: '/masters/hardware-configuration',
    icon: <SettingsInputComponentIcon fontSize="small" />,
    permission: PERMISSIONS.WEIGHT_PRODUCTION_MANAGE,
    configKey: 'weightProductionEnabled',
  },
  {
    labelKey: 'hardwareMonitor',
    to: '/masters/hardware-monitor',
    icon: <MonitorHeartIcon fontSize="small" />,
    permission: PERMISSIONS.WEIGHT_PRODUCTION_VIEW,
    configKey: 'weightProductionEnabled',
  },
  {
    labelKey: 'dispatchOrder',
    to: '/transactions/dispatch-orders',
    icon: <Inventory2Icon fontSize="small" />,
    permission: PERMISSIONS.DISPATCH_ORDER_VIEW,
    configKey: 'dispatchEnabled',
  },
  {
    // Reuses the generic Picking.View/.Execute codes, not a Dispatch-specific permission — see
    // DispatchPickList/DispatchMaterialIssue's doc comments for why.
    labelKey: 'dispatchPickList',
    to: '/transactions/dispatch-pick-list',
    icon: <ListAltIcon fontSize="small" />,
    permission: PERMISSIONS.PICKING_VIEW,
    configKey: 'dispatchEnabled',
  },
  {
    labelKey: 'dispatchMaterialIssue',
    to: '/transactions/dispatch-material-issue',
    icon: <OutputIcon fontSize="small" />,
    permission: PERMISSIONS.PICKING_VIEW,
    configKey: 'dispatchEnabled',
  },
  {
    labelKey: 'packing',
    to: '/transactions/packing',
    icon: <AllInboxIcon fontSize="small" />,
    permission: PERMISSIONS.PACKING_VIEW,
    configKey: 'packingEnabled',
  },
  {
    labelKey: 'packingLabelPrint',
    to: '/transactions/packing-label-printing',
    icon: <LocalPrintshopIcon fontSize="small" />,
    permission: PERMISSIONS.PACKING_VIEW,
    // Barcode Required is the sole switch for every label-print screen (see labelPrinting's own
    // identical rationale below) — Packing Label has no independent manual toggle anymore.
    configKey: ['packingEnabled', 'barcodeMandatory'],
  },
  {
    labelKey: 'loading',
    to: '/transactions/loading',
    icon: <OutboundIcon fontSize="small" />,
    permission: PERMISSIONS.LOADING_VIEW,
  },
  {
    labelKey: 'gateExit',
    to: '/transactions/gate-exit',
    icon: <ExitToAppIcon fontSize="small" />,
    permission: PERMISSIONS.GATE_EXIT_VIEW,
    configKey: 'gateExitEnabled',
  },
  {
    labelKey: 'stockAdjustment',
    to: '/transactions/stock-adjustment',
    icon: <BalanceIcon fontSize="small" />,
    permission: PERMISSIONS.STOCK_ADJUSTMENT_VIEW,
    configKey: 'stockAdjustmentEnabled',
  },
  {
    labelKey: 'stockTransfer',
    to: '/transactions/stock-transfers',
    icon: <SwapHorizIcon fontSize="small" />,
    permission: PERMISSIONS.STOCK_TRANSFER_VIEW,
    configKey: 'stockTransferEnabled',
  },
  {
    labelKey: 'cycleCount',
    to: '/transactions/cycle-counts',
    icon: <AssignmentTurnedInIcon fontSize="small" />,
    permission: PERMISSIONS.CYCLE_COUNT_VIEW,
    configKey: 'cycleCountEnabled',
  },
  {
    labelKey: 'returnNote',
    to: '/transactions/returns',
    icon: <UndoIcon fontSize="small" />,
    permission: PERMISSIONS.RETURN_NOTE_VIEW,
    configKey: 'returnNoteEnabled',
  },
  {
    // Only meaningful once this warehouse company is invoicing Clients for storage/handling/VAS —
    // meaningless with 3PL off, so gated exactly the opposite way to Purchase/Production above (a
    // configKey it needs, not a hiddenWhenConfigKey it's excluded by). BillingEnabled is its own
    // independent sub-switch of 3PL (Process Configuration → Third-Party Logistics card) — a company
    // can have 3PL client-scoping on without yet turning on invoicing, so both flags are required.
    labelKey: 'billing',
    to: '/transactions/billing',
    icon: <ReceiptLongIcon fontSize="small" />,
    permission: PERMISSIONS.BILLING_VIEW,
    configKey: ['thirdPartyLogisticsEnabled', 'billingEnabled'],
  },
  {
    labelKey: 'reportsInventory',
    to: '/reports/inventory',
    icon: <AssessmentIcon fontSize="small" />,
    permission: PERMISSIONS.REPORT_VIEW,
  },
  {
    // Genealogy (supplier<->customer) and single-batch history — split out of Inventory Reports
    // into its own module since neither is a stock-position report.
    labelKey: 'reportsTraceability',
    to: '/reports/traceability',
    icon: <AssessmentIcon fontSize="small" />,
    permission: PERMISSIONS.REPORT_VIEW,
  },
  {
    labelKey: 'reportsProcurement',
    to: '/reports/procurement',
    icon: <AssessmentIcon fontSize="small" />,
    permission: PERMISSIONS.REPORT_VIEW,
  },
  {
    labelKey: 'reportsInboundQuality',
    to: '/reports/inbound-quality',
    icon: <AssessmentIcon fontSize="small" />,
    permission: PERMISSIONS.REPORT_VIEW,
  },
  {
    labelKey: 'reportsOutbound',
    to: '/reports/outbound',
    icon: <AssessmentIcon fontSize="small" />,
    permission: PERMISSIONS.REPORT_VIEW,
  },
  {
    labelKey: 'reportsProduction',
    to: '/reports/production',
    icon: <AssessmentIcon fontSize="small" />,
    permission: PERMISSIONS.REPORT_VIEW,
  },
  {
    // Nothing to report on until this company actually subcontracts — hidden with the module off,
    // the same treatment Client Summary gets for 3PL.
    labelKey: 'reportsJobWork',
    to: '/reports/job-work',
    icon: <AssessmentIcon fontSize="small" />,
    permission: PERMISSIONS.REPORT_VIEW,
    configKey: 'jobWorkEnabled',
  },
  {
    // Same treatment as Job Work above — hidden until the company turns Weigh & Label on.
    labelKey: 'reportsWeightProduction',
    to: '/reports/weight-production',
    icon: <AssessmentIcon fontSize="small" />,
    permission: PERMISSIONS.REPORT_VIEW,
    configKey: 'weightProductionEnabled',
  },
  {
    labelKey: 'reportsAdjustments',
    to: '/reports/adjustments',
    icon: <AssessmentIcon fontSize="small" />,
    permission: PERMISSIONS.REPORT_VIEW,
  },
  {
    labelKey: 'reportsApprovals',
    to: '/reports/approvals',
    icon: <AssessmentIcon fontSize="small" />,
    permission: PERMISSIONS.REPORT_VIEW,
  },
  {
    // Only meaningful once this warehouse is invoicing/tracking Clients — the first Reports item
    // to carry a configKey at all, since every other report stays company-wide regardless of 3PL.
    labelKey: 'reportsClientSummary',
    to: '/reports/client-summary',
    icon: <AssessmentIcon fontSize="small" />,
    permission: PERMISSIONS.REPORT_VIEW,
    configKey: 'thirdPartyLogisticsEnabled',
  },
  {
    // Designing label layouts has nothing to print against once Barcode Required is off — no
    // screen ever demands a scan, so there's no barcode/label to design for.
    labelKey: 'labelDesigner',
    to: '/label-designer',
    icon: <DescriptionOutlinedIcon fontSize="small" />,
    permission: PERMISSIONS.LABEL_TEMPLATE_VIEW,
    configKey: 'barcodeMandatory',
  },
  {
    labelKey: 'labelPrinting',
    to: '/transactions/label-printing',
    icon: <LocalPrintshopIcon fontSize="small" />,
    permission: PERMISSIONS.LABEL_TEMPLATE_VIEW,
    // Barcode Required off means nothing downstream (QC, Putaway) ever checks a barcode, so
    // there's no reason to print one either — printing labels nobody will scan just leaves
    // orphaned PrintedLabel rows (see PutawayService.ConfirmAsync's BarcodeMandatory gate).
    // EnableGrnLabelPrinting is no longer an independent manual toggle — ProcessConfigurationService
    // always forces it to mirror BarcodeMandatory server-side, so gating on BarcodeMandatory alone
    // is equivalent and simpler. Also hidden once Raw Material Handling is off, same as GRN/QC/
    // Putaway above — this is specifically the GRN-context print screen (Job Work/Production/
    // Packing each have their own separate label-printing routes, untouched by this flag).
    configKey: ['barcodeMandatory', 'weightProductionRawMaterialHandlingEnabled'],
  },
  {
    labelKey: 'companies',
    to: '/admin/companies',
    icon: <CorporateFareIcon fontSize="small" />,
    role: ROLES.SUPER_ADMIN,
  },
  {
    // Cross-tenant login/activity feed — SuperAdmin only, same gate as Companies above. Distinct
    // from the per-company AuditLog.View permission (see AuditLogController): that one only ever
    // shows a company its own entries.
    labelKey: 'activityLog',
    to: '/admin/activity-log',
    icon: <HistoryIcon fontSize="small" />,
    role: ROLES.SUPER_ADMIN,
  },
  {
    labelKey: 'users',
    to: '/admin/users',
    icon: <PeopleIcon fontSize="small" />,
    permission: PERMISSIONS.USER_VIEW,
  },
  {
    labelKey: 'approvalMatrix',
    to: '/admin/approval-matrix',
    icon: <RuleIcon fontSize="small" />,
    permission: PERMISSIONS.APPROVAL_MATRIX_VIEW,
  },
];

// Purely a sidebar-grouping/display concern — doesn't affect which items exist, their routes,
// permissions, or config gating (all still resolved from NAV_ITEMS above). Items not listed here
// (Dashboard, Masters) render as ungrouped top-level links, same as the reference template's
// always-visible entries above its collapsible categories.
const NAV_GROUPS: { titleKey: string; accent: Accent; icon: ReactElement; labelKeys: string[] }[] = [
  {
    titleKey: 'groups.procurement',
    accent: 'secondary',
    icon: <ShoppingCartIcon fontSize="small" />,
    labelKeys: ['purchaseRequest', 'purchaseOrder', 'gateEntry'],
  },
  {
    titleKey: 'groups.inboundQuality',
    accent: 'primary',
    icon: <MoveToInboxIcon fontSize="small" />,
    labelKeys: ['grn', 'qc', 'putaway', 'warehouseTransferReceipt', 'labelPrinting'],
  },
  {
    titleKey: 'groups.production',
    accent: 'warning',
    icon: <PrecisionManufacturingIcon fontSize="small" />,
    labelKeys: ['materialRequest', 'picking', 'productionOrders', 'productionPickList', 'materialIssue', 'productionReceipt', 'productionLabelPrint', 'productionPutaway'],
  },
  {
    titleKey: 'groups.jobWork',
    accent: 'warning',
    icon: <HandymanIcon fontSize="small" />,
    labelKeys: ['jobWorkOrders', 'jobWorkIssue', 'jobWorkReceipt', 'jobWorkQc', 'jobWorkLabelPrint', 'jobWorkPutaway'],
  },
  {
    titleKey: 'groups.weightProduction',
    accent: 'warning',
    icon: <ScaleIcon fontSize="small" />,
    labelKeys: ['weightProductionPlan', 'weightProductionIssue', 'weightProductionKiosk', 'weightProductionJobCompletion', 'weightProductionPutaway', 'weightProductionLabelPrint', 'hardwareConfiguration', 'hardwareMonitor'],
  },
  {
    titleKey: 'groups.outbound',
    accent: 'error',
    icon: <OutboundIcon fontSize="small" />,
    labelKeys: ['dispatchOrder', 'dispatchPickList', 'dispatchMaterialIssue', 'packing', 'packingLabelPrint', 'loading', 'gateExit'],
  },
  {
    titleKey: 'groups.adjustments',
    accent: 'primary',
    icon: <BalanceIcon fontSize="small" />,
    labelKeys: ['stockAdjustment', 'stockTransfer', 'cycleCount', 'returnNote'],
  },
  {
    titleKey: 'groups.billing',
    accent: 'warning',
    icon: <ReceiptLongIcon fontSize="small" />,
    labelKeys: ['billing'],
  },
  {
    titleKey: 'groups.reports',
    accent: 'secondary',
    icon: <AssessmentIcon fontSize="small" />,
    labelKeys: [
      'reportsInventory', 'reportsTraceability', 'reportsProcurement', 'reportsInboundQuality', 'reportsOutbound',
      'reportsProduction', 'reportsJobWork', 'reportsWeightProduction', 'reportsAdjustments', 'reportsApprovals', 'reportsClientSummary',
    ],
  },
  {
    titleKey: 'groups.tools',
    accent: 'secondary',
    icon: <DescriptionOutlinedIcon fontSize="small" />,
    labelKeys: ['labelDesigner'],
  },
  {
    titleKey: 'groups.administration',
    accent: 'warning',
    icon: <TuneIcon fontSize="small" />,
    labelKeys: ['companies', 'activityLog', 'users', 'approvalMatrix'],
  },
];

function NavRow({
  item,
  label,
  selected,
  collapsed,
  accent,
}: {
  item: NavLeaf;
  label: string;
  selected: boolean;
  collapsed: boolean;
  accent?: Accent;
}) {
  return (
    <ListItemButton
      component={RouterLink}
      to={item.to}
      selected={selected}
      title={collapsed ? label : undefined}
      sx={{
        borderRadius: '2px',
        height: NAV_ROW_HEIGHT,
        minHeight: NAV_ROW_HEIGHT,
        flexShrink: 0,
        py: 0,
        alignItems: 'center',
        justifyContent: collapsed ? 'center' : 'flex-start',
        px: collapsed ? 1 : 1.5,
        position: 'relative',
        color: selected ? `${accent ?? 'primary'}.main` : 'text.secondary',
        fontWeight: selected ? 700 : 500,
        '&:hover': { bgcolor: 'action.hover' },
        '&.Mui-selected': { bgcolor: (theme) => `${theme.palette[accent ?? 'primary'].main}14` },
        '&.Mui-selected:hover': { bgcolor: (theme) => `${theme.palette[accent ?? 'primary'].main}20` },
        '&::before': selected
          ? {
              content: '""',
              position: 'absolute',
              left: 0,
              top: '20%',
              bottom: '20%',
              width: 3,
              borderRadius: '2px',
              bgcolor: `${accent ?? 'primary'}.main`,
            }
          : undefined,
      }}
    >
      <ListItemIcon sx={{ minWidth: collapsed ? 0 : 36, color: 'inherit' }}>{item.icon}</ListItemIcon>
      {!collapsed && <ListItemText primary={label} slotProps={{ primary: { fontSize: 14, fontWeight: 'inherit' } }} />}
    </ListItemButton>
  );
}

function NavGroup({
  titleKey,
  accent,
  icon,
  items,
  labelOf,
  isActive,
  collapsed,
  open,
  onToggle,
}: {
  titleKey: string;
  accent: Accent;
  icon: ReactElement;
  items: NavLeaf[];
  labelOf: (item: NavLeaf) => string;
  isActive: (item: NavLeaf) => boolean;
  collapsed: boolean;
  // Lifted to AppShell so opening one group can close whichever other group was open — an
  // accordion, not independently-toggled panels.
  open: boolean;
  onToggle: () => void;
}) {
  const { t } = useTranslation('nav');

  if (items.length === 0) return null;

  if (collapsed) {
    return (
      <Box
        sx={{
          pt: 1,
          mt: 1,
          borderTop: 1,
          borderColor: 'divider',
          display: 'flex',
          flexDirection: 'column',
          gap: 0.5,
          flexShrink: 0,
          '&:first-of-type': { borderTop: 0, pt: 0, mt: 0 },
        }}
      >
        {items.map((item) => (
          <NavRow key={item.to} item={item} label={labelOf(item)} selected={isActive(item)} collapsed accent={accent} />
        ))}
      </Box>
    );
  }

  return (
    <Box
      sx={{
        borderRadius: '2px',
        bgcolor: 'background.paper',
        border: 1,
        borderColor: 'divider',
        boxShadow: 1,
        overflow: 'hidden',
        // The parent nav List is a flex column — without this, a flex item taller than the
        // List's own allocated height gets proportionally *shrunk* by the browser instead of
        // just letting the List scroll, which is what was compressing the last row of whichever
        // group happened to be expanded.
        flexShrink: 0,
      }}
    >
      <ListItemButton
        onClick={onToggle}
        sx={{ height: NAV_ROW_HEIGHT, minHeight: NAV_ROW_HEIGHT, flexShrink: 0, py: 0, px: 1.25, alignItems: 'center', '&:hover': { bgcolor: 'action.hover' } }}
      >
        <Box
          sx={{
            width: 24,
            height: 24,
            borderRadius: '2px',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            flexShrink: 0,
            mr: 1.25,
            bgcolor: `${accent}.main`,
            color: `${accent}.contrastText`,
            '& svg': { fontSize: 14 },
          }}
        >
          {icon}
        </Box>
        <Typography variant="caption" sx={{ flex: 1, textTransform: 'uppercase', letterSpacing: '0.06em', fontWeight: 700 }}>
          {t(titleKey)}
        </Typography>
        <ExpandMoreIcon fontSize="small" sx={{ transform: open ? 'none' : 'rotate(-90deg)', transition: 'transform 0.15s' }} />
      </ListItemButton>
      <Collapse in={open}>
        <Divider />
        <Box sx={{ px: 0.75, py: 0.75, display: 'flex', flexDirection: 'column', gap: 0.5, flexShrink: 0 }}>
          {items.map((item) => (
            <NavRow key={item.to} item={item} label={labelOf(item)} selected={isActive(item)} collapsed={false} accent={accent} />
          ))}
        </Box>
      </Collapse>
    </Box>
  );
}

export default function AppShell() {
  const { t } = useTranslation('nav');
  const { user, hasPermission, hasRole, logout } = useAuth();
  const { activeCompanyName } = useActiveCompany();
  // An ordinary company user always shows their own company; a company-less SuperAdmin has none
  // of their own, so the top bar instead reflects whichever company a nested page (e.g. the
  // per-company Process Configuration screen) has announced via ActiveCompanyContext.
  const displayedCompanyName = user?.companyName ?? activeCompanyName;
  const { mode, toggleMode } = useThemeMode();
  const location = useLocation();
  const navigate = useNavigate();
  // Nav gating only ever applies to an ordinary company user, reflecting their own company's config
  // (self-service route, `undefined` here keeps the hook's default behavior). A company-less
  // SuperAdmin always sees every screen in their own left nav, full stop — Process Configuration is
  // a per-company concern for the company being administered, not a filter on the SuperAdmin's own
  // navigation, even while they're actively editing one specific company's config (e.g. on
  // CompanyProcessConfigurationPage). `null` here skips the fetch entirely, so `processConfiguration`
  // stays undefined and every configKey-gated item passes (see passesConfigGate's own fallback).
  const { data: processConfiguration } = useProcessConfiguration(user?.companyId ? undefined : null);

  const [userMenuAnchor, setUserMenuAnchor] = useState<HTMLElement | null>(null);
  const [collapsed, setCollapsed] = useState(() => localStorage.getItem(SIDEBAR_COLLAPSED_KEY) === 'true');

  // Below md the sidebar can't be permanent — 260px of a 360px phone screen leaves nothing for the
  // page itself. It becomes an overlay drawer opened from the header's hamburger instead, and is
  // never rendered in the narrow icons-only "collapsed" form there: on a touch overlay there's no
  // space to reclaim, so the full labelled sidebar is simply better.
  const isMobile = useMediaQuery((theme: Theme) => theme.breakpoints.down('md'));
  const [mobileNavOpen, setMobileNavOpen] = useState(false);

  // A tap that navigates has to close the overlay, or the destination page stays hidden behind it.
  useEffect(() => {
    setMobileNavOpen(false);
  }, [location.pathname]);

  // Accordion: only one group open at a time. Starts on whichever group contains the current
  // route (so a direct link into e.g. Outbound doesn't land with that group hidden), falling back
  // to the first group otherwise.
  const [openGroup, setOpenGroup] = useState<string | null>(() => {
    const activeGroup = NAV_GROUPS.find((group) =>
      group.labelKeys.some((key) => {
        const item = NAV_ITEMS.find((navItem) => navItem.labelKey === key);
        return item ? location.pathname.startsWith(item.to) : false;
      }),
    );
    return (activeGroup ?? NAV_GROUPS[0])?.titleKey ?? null;
  });

  const toggleCollapsed = () => {
    setCollapsed((prev) => {
      const next = !prev;
      localStorage.setItem(SIDEBAR_COLLAPSED_KEY, String(next));
      return next;
    });
  };

  // weightProductionRawMaterialHandlingEnabled only means anything once Weigh & Label itself
  // (weightProductionEnabled) is on — it exists purely to hide GRN/QC/Putaway/GRN Label Print for a
  // Weigh & Label plant running Direct-only plans (nothing ever issued via a raw-material GRN). A
  // company that doesn't use Weigh & Label at all must never have those screens hidden just because
  // this sub-flag happens to be false for it; the flag is only ever consulted when its parent module
  // is actually enabled, exactly like every other master-switch/sub-flag pair in this list.
  const getEffectiveConfigValue = (key: keyof ProcessConfigurationDto): boolean => {
    if (!processConfiguration) return false;
    if (key === 'weightProductionRawMaterialHandlingEnabled' && !processConfiguration.weightProductionEnabled) {
      return true;
    }
    return Boolean(processConfiguration[key]);
  };

  // A config-gated item stays visible until the configuration has actually loaded (avoids a
  // flash-hide on first render) — once loaded, every one of its flag(s) must be true.
  const passesConfigGate = (item: NavLeaf) => {
    if (!item.configKey || !processConfiguration) return true;
    const keys = Array.isArray(item.configKey) ? item.configKey : [item.configKey];
    return keys.every((key) => getEffectiveConfigValue(key));
  };

  // Companion OR-group gate — passes if ANY of anyConfigKey's flags are true (vs passesConfigGate's
  // "every one"). Undefined/empty anyConfigKey always passes, same fallback convention as the
  // other two gates.
  const passesAnyConfigGate = (item: NavLeaf) => {
    if (!item.anyConfigKey || item.anyConfigKey.length === 0 || !processConfiguration) return true;
    return item.anyConfigKey.some((key) => processConfiguration[key]);
  };

  // Inverse of the gate above — hides the item once any of its hiddenWhenConfigKey flag(s) come on
  // (e.g. Purchase Request/Order and the whole Production module once 3PL is enabled).
  const passesHiddenWhenGate = (item: NavLeaf) => {
    if (!item.hiddenWhenConfigKey || !processConfiguration) return true;
    const keys = Array.isArray(item.hiddenWhenConfigKey) ? item.hiddenWhenConfigKey : [item.hiddenWhenConfigKey];
    return !keys.some((key) => processConfiguration[key]);
  };

  const visibleItems = NAV_ITEMS.filter(
    (item) =>
      (!item.permission || hasPermission(item.permission)) &&
      (!item.role || hasRole(item.role)) &&
      passesConfigGate(item) &&
      passesAnyConfigGate(item) &&
      passesHiddenWhenGate(item) &&
      (!item.customGate || item.customGate(processConfiguration)),
  );

  // GRN Label Print's position relative to Quality Control tracks
  // ProcessConfiguration.grnLabelPrintTiming: "BeforeQc" (printing happens ahead of inspection)
  // sorts the nav entry just before QC; "AfterQc" (printing only allowed once QC has passed, the
  // default) sorts it just after. Every other item keeps its original relative order. This still
  // runs on the flat list before grouping below, so the grouped Inbound & Quality section renders
  // labelPrinting in the resolved position relative to qc.
  const orderedItems = [...visibleItems];
  const labelPrintingIndex = orderedItems.findIndex((item) => item.labelKey === 'labelPrinting');
  const qcIndex = orderedItems.findIndex((item) => item.labelKey === 'qc');
  if (labelPrintingIndex !== -1 && qcIndex !== -1 && labelPrintingIndex !== qcIndex) {
    const [labelPrintingItem] = orderedItems.splice(labelPrintingIndex, 1);
    const adjustedQcIndex = orderedItems.findIndex((item) => item.labelKey === 'qc');
    const insertAt = processConfiguration?.grnLabelPrintTiming === 'BeforeQc' ? adjustedQcIndex : adjustedQcIndex + 1;
    orderedItems.splice(insertAt, 0, labelPrintingItem);
  }

  const groupedLabelKeys = new Set(NAV_GROUPS.flatMap((g) => g.labelKeys));
  const topLevelItems = orderedItems.filter((item) => !groupedLabelKeys.has(item.labelKey));
  const labelOf = (item: NavLeaf) => t(item.labelKey);
  const isActive = (item: NavLeaf) => location.pathname.startsWith(item.to);

  // Collapsed is a desktop-only affordance (see isMobile above) — on the overlay drawer the full
  // labelled sidebar always wins.
  const isCollapsed = collapsed && !isMobile;
  const drawerWidth = isCollapsed ? DRAWER_WIDTH_COLLAPSED : DRAWER_WIDTH;

  return (
    <Box sx={{ display: 'flex', minHeight: '100vh' }}>
      <Drawer
        variant={isMobile ? 'temporary' : 'permanent'}
        open={isMobile ? mobileNavOpen : true}
        onClose={() => setMobileNavOpen(false)}
        // Keeps the nav in the DOM between opens — on a phone this is the difference between the
        // drawer appearing instantly and re-mounting the whole tree on every tap.
        ModalProps={{ keepMounted: true }}
        sx={{
          width: isMobile ? 0 : drawerWidth,
          flexShrink: 0,
          transition: (theme) => theme.transitions.create('width', { duration: theme.transitions.duration.shorter }),
          [`& .MuiDrawer-paper`]: {
            width: drawerWidth,
            boxSizing: 'border-box',
            border: 'none',
            // Left visible (not hidden) — the collapse-toggle button below is deliberately pinned
            // half-outside the sidebar's right edge, and clipping overflow here was cutting it off.
            overflow: 'visible',
            transition: (theme) => theme.transitions.create('width', { duration: theme.transitions.duration.shorter }),
          },
        }}
      >
        <Toolbar sx={{ px: isCollapsed ? 1.5 : 3, py: 4, position: 'relative', justifyContent: 'center' }}>
          <BrandMark collapsed={isCollapsed} />
        </Toolbar>

        {/* Collapse toggle is desktop-only — the overlay drawer is dismissed by tapping away or
            navigating, and has no adjacent content to reclaim width from. */}
        {!isMobile && (
          <Box sx={{ position: 'relative' }}>
            <Tooltip title={isCollapsed ? t('expandSidebar') : t('collapseSidebar')}>
              <IconButton
                size="small"
                onClick={toggleCollapsed}
                sx={{
                  position: 'absolute',
                  right: -12,
                  top: -20,
                  width: 24,
                  height: 24,
                  bgcolor: 'background.paper',
                  border: 1,
                  borderColor: 'divider',
                  boxShadow: 1,
                  zIndex: 10,
                  '&:hover': { bgcolor: 'background.paper' },
                }}
              >
                {isCollapsed ? <ChevronRightIcon sx={{ fontSize: 16 }} /> : <ChevronLeftIcon sx={{ fontSize: 16 }} />}
              </IconButton>
            </Tooltip>
          </Box>
        )}

        <List
          sx={{
            px: isCollapsed ? 1 : 1.75,
            py: 1,
            flex: 1,
            overflowY: 'auto',
            overflowX: 'hidden',
            display: 'flex',
            flexDirection: 'column',
            gap: 1.5,
            // A thin, low-profile scrollbar for the nav list — present when scrolling is needed,
            // but not visually competing with the nav content.
            '&::-webkit-scrollbar': { width: 4 },
            '&::-webkit-scrollbar-track': { background: 'transparent' },
            '&::-webkit-scrollbar-thumb': {
              backgroundColor: (theme) => theme.palette.action.disabled,
              borderRadius: '2px',
            },
            '&::-webkit-scrollbar-thumb:hover': { backgroundColor: 'text.secondary' },
            scrollbarWidth: 'thin',
          }}
        >
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5, flexShrink: 0 }}>
            {topLevelItems.map((item) => (
              <NavRow key={item.to} item={item} label={labelOf(item)} selected={isActive(item)} collapsed={isCollapsed} />
            ))}
          </Box>

          {NAV_GROUPS.map((group) => (
            <NavGroup
              key={group.titleKey}
              titleKey={group.titleKey}
              accent={group.accent}
              icon={group.icon}
              items={orderedItems.filter((item) => group.labelKeys.includes(item.labelKey))}
              labelOf={labelOf}
              isActive={isActive}
              collapsed={isCollapsed}
              open={openGroup === group.titleKey}
              onToggle={() => setOpenGroup((prev) => (prev === group.titleKey ? null : group.titleKey))}
            />
          ))}
        </List>
      </Drawer>

      {/* minWidth: 0 — same flex-shrink fix as the inner main Box below; this is the OUTER flex
          item (row: Drawer + this column), so without it here too, this whole column refuses to
          shrink below its own widest descendant's natural width, overriding the inner fix. */}
      <Box sx={{ flexGrow: 1, minWidth: 0, display: 'flex', flexDirection: 'column' }}>
        <AppBar position="sticky" elevation={0}>
          <Toolbar sx={{ justifyContent: 'flex-end', gap: 1 }}>
            {/* The only way to reach the nav below md, where the sidebar is an overlay. */}
            {isMobile && (
              <IconButton edge="start" onClick={() => setMobileNavOpen(true)} aria-label={t('openSidebar')} sx={{ mr: 0.5 }}>
                <MenuIcon />
              </IconButton>
            )}
            {displayedCompanyName ? (
              <Box sx={{ flexGrow: 1, minWidth: 0, display: 'flex' }}>
                <Stack
                  direction="row"
                  spacing={1}
                  alignItems="center"
                  sx={{
                    minWidth: 0,
                    maxWidth: '100%',
                    pl: 0.75,
                    pr: 1.75,
                    py: 0.5,
                    borderRadius: 999,
                    bgcolor: (theme) => (theme.palette.mode === 'dark' ? 'rgba(5, 102, 217, 0.16)' : 'rgba(5, 102, 217, 0.08)'),
                    border: '1px solid',
                    borderColor: (theme) => (theme.palette.mode === 'dark' ? 'rgba(5, 102, 217, 0.35)' : 'rgba(5, 102, 217, 0.18)'),
                  }}
                >
                  <Box
                    sx={{
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      width: 26,
                      height: 26,
                      borderRadius: '50%',
                      bgcolor: 'primary.main',
                      color: 'primary.contrastText',
                      flexShrink: 0,
                    }}
                  >
                    <CorporateFareIcon sx={{ fontSize: 15 }} />
                  </Box>
                  <Typography
                    variant="subtitle2"
                    fontWeight={700}
                    noWrap
                    sx={{ letterSpacing: 0.2, color: 'primary.main' }}
                  >
                    {displayedCompanyName}
                  </Typography>
                </Stack>
              </Box>
            ) : (
              <Box sx={{ flexGrow: 1 }} />
            )}
            <NetworkStatusIndicator />
            <NotificationBell />
            {(processConfiguration?.tvDisplayEnabled ?? true) && (
              <Tooltip title={t('openTvDisplay')}>
                <IconButton size="small" onClick={() => window.open('/tv-dashboard', '_blank')}>
                  <TvOutlinedIcon fontSize="small" />
                </IconButton>
              </Tooltip>
            )}
            <Tooltip title={mode === 'dark' ? t('lightMode') : t('darkMode')}>
              <IconButton size="small" onClick={toggleMode}>
                {mode === 'dark' ? <LightModeOutlinedIcon fontSize="small" /> : <DarkModeOutlinedIcon fontSize="small" />}
              </IconButton>
            </Tooltip>
            <LanguageSwitcher />
            <Divider orientation="vertical" flexItem sx={{ my: 1.5, mx: 0.5 }} />
            <Stack
              direction="row"
              spacing={1.25}
              alignItems="center"
              onClick={(e) => setUserMenuAnchor(e.currentTarget)}
              sx={{ cursor: 'pointer', borderRadius: '2px', px: 1, py: 0.5, '&:hover': { bgcolor: 'action.hover' } }}
            >
              <Avatar src={user?.avatarUrl ?? undefined} sx={{ width: 32, height: 32, bgcolor: user?.avatarColor, fontSize: 14 }}>
                {user ? getInitials(user) : undefined}
              </Avatar>
              <Box sx={{ display: { xs: 'none', md: 'block' } }}>
                <Typography variant="body2" fontWeight={700} sx={{ lineHeight: 1.2 }}>
                  {user?.fullName}
                </Typography>
                <Typography variant="caption" color="text.secondary">
                  {user?.roles.join(', ')}
                </Typography>
              </Box>
            </Stack>
            <Menu anchorEl={userMenuAnchor} open={Boolean(userMenuAnchor)} onClose={() => setUserMenuAnchor(null)}>
              <MenuItem
                onClick={() => {
                  setUserMenuAnchor(null);
                  navigate('/profile');
                }}
              >
                <ListItemIcon>
                  <PersonOutlineIcon fontSize="small" />
                </ListItemIcon>
                {t('myProfile')}
              </MenuItem>
              <MenuItem
                onClick={() => {
                  setUserMenuAnchor(null);
                  void logout();
                }}
              >
                <ListItemIcon>
                  <LogoutIcon fontSize="small" />
                </ListItemIcon>
                {t('logout')}
              </MenuItem>
            </Menu>
          </Toolbar>
        </AppBar>

        {/* minWidth: 0 is the actual fix for wide-content overflow across the whole app (tab bars,
            wide tables, ...) — a flex item with flexGrow but no minWidth defaults to
            min-width: auto, which refuses to shrink below its content's natural width. Without
            this, any page whose content wants more horizontal room than available (e.g. the
            Masters screen's scrollable Tabs once there are enough master tabs to overflow) just
            grows this whole main area — and the page itself — wider than the viewport, instead of
            capping it here and letting that content's own internal overflow-x/scroll behavior
            (which Tabs already implements) actually engage. */}
        <Box component="main" sx={{ flexGrow: 1, minWidth: 0, p: { xs: 2, md: 3 } }}>
          <Outlet />
        </Box>
      </Box>
    </Box>
  );
}
